import React, { useState } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../../contexts/AdminAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PagosTab({ userData, refreshData }) {
  const { getToken } = useAdminAuth();
  const { user, payments, transactions, summary } = userData;
  
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);

  const handleRetryPayment = async (paymentId) => {
    if (!window.confirm('¿Reintentar cobro para este pago?')) return;
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/payments/${paymentId}/retry`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      alert('Reintento de cobro iniciado');
      refreshData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al reintentar cobro');
    }
  };

  const openRefundModal = (payment) => {
    setSelectedPayment(payment);
    setShowRefundModal(true);
  };

  const downloadReceipt = (payment) => {
    // Generate receipt URL
    const receiptUrl = payment.receipt_url || `${BACKEND_URL}/api/receipts/${payment.id}`;
    window.open(receiptUrl, '_blank');
  };

  // Calculate stats
  const completedPayments = payments?.filter(p => p.status === 'completed') || [];
  const failedPayments = payments?.filter(p => p.status === 'failed') || [];
  const pendingPayments = payments?.filter(p => p.status === 'pending') || [];

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-black/30 rounded-lg p-4 border border-[#1a4d5c]/30">
          <div className="text-xs text-gray-400 mb-1">Total Pagado</div>
          <div className="text-green-400 font-bold text-2xl">${summary.total_paid}</div>
        </div>
        <div className="bg-black/30 rounded-lg p-4 border border-[#1a4d5c]/30">
          <div className="text-xs text-gray-400 mb-1">Balance Pendiente</div>
          <div className="text-yellow-400 font-bold text-2xl">${summary.balance}</div>
        </div>
        <div className="bg-black/30 rounded-lg p-4 border border-[#1a4d5c]/30">
          <div className="text-xs text-gray-400 mb-1">Pagos Exitosos</div>
          <div className="text-white font-bold text-2xl">{completedPayments.length}</div>
        </div>
        <div className="bg-black/30 rounded-lg p-4 border border-[#1a4d5c]/30">
          <div className="text-xs text-gray-400 mb-1">Pagos Fallidos</div>
          <div className="text-red-400 font-bold text-2xl">{failedPayments.length}</div>
        </div>
      </div>

      {/* Payment Records Table */}
      <div className="bg-black/30 rounded-lg border border-[#1a4d5c]/30 overflow-hidden">
        <div className="px-6 py-4 border-b border-[#1a4d5c]/30">
          <h3 className="text-lg font-bold text-white">Historial de Pagos</h3>
        </div>
        
        {payments && payments.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-black/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs text-gray-400 uppercase">Fecha</th>
                  <th className="px-4 py-3 text-left text-xs text-gray-400 uppercase">ID/Referencia</th>
                  <th className="px-4 py-3 text-left text-xs text-gray-400 uppercase">Concepto</th>
                  <th className="px-4 py-3 text-left text-xs text-gray-400 uppercase">Monto</th>
                  <th className="px-4 py-3 text-left text-xs text-gray-400 uppercase">Estado</th>
                  <th className="px-4 py-3 text-left text-xs text-gray-400 uppercase">Método</th>
                  <th className="px-4 py-3 text-left text-xs text-gray-400 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#1a4d5c]/30">
                {payments.map((payment) => (
                  <tr key={payment.id} className="hover:bg-[#1a4d5c]/10">
                    <td className="px-4 py-3 text-sm text-gray-300">
                      {new Date(payment.created_at).toLocaleDateString('es-ES', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-white font-mono">{payment.id}</div>
                      {payment.reference && (
                        <div className="text-xs text-gray-500">Ref: {payment.reference}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-white">
                      {payment.type === 'manual' ? '💰 Pago Manual' : 
                       payment.type === 'enrollment_fee' ? '📝 Inscripción' :
                       payment.type === 'installment' ? '💳 Cuota' : '💳 Pago Completo'}
                    </td>
                    <td className="px-4 py-3 text-sm font-bold text-white">
                      ${payment.amount?.toFixed(2) || '0.00'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        payment.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                        payment.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                        payment.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                        'bg-gray-500/20 text-gray-400'
                      }`}>
                        {payment.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-300 capitalize">
                      {payment.payment_method?.replace('_', ' ') || payment.method || 'stripe'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        {payment.status === 'completed' && (
                          <>
                            <button
                              onClick={() => downloadReceipt(payment)}
                              className="text-blue-400 hover:text-blue-300 text-xs"
                              title="Descargar recibo"
                            >
                              📄 Recibo
                            </button>
                            <button
                              onClick={() => openRefundModal(payment)}
                              className="text-red-400 hover:text-red-300 text-xs"
                              title="Reembolsar"
                            >
                              ↩️ Reembolso
                            </button>
                          </>
                        )}
                        {payment.status === 'failed' && (
                          <button
                            onClick={() => handleRetryPayment(payment.id)}
                            className="text-yellow-400 hover:text-yellow-300 text-xs"
                            title="Reintentar cobro"
                          >
                            🔄 Reintentar
                          </button>
                        )}
                        {payment.status === 'pending' && (
                          <span className="text-xs text-gray-500">⏳ Procesando</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-400">No hay pagos registrados</p>
          </div>
        )}
      </div>

      {/* Stripe Transactions */}
      {transactions && transactions.length > 0 && (
        <div className="bg-black/30 rounded-lg p-6 border border-[#1a4d5c]/30">
          <h4 className="font-semibold text-white mb-4 flex items-center gap-2">
            <span>Transacciones Stripe</span>
            <span className="text-xs text-gray-500">({transactions.length})</span>
          </h4>
          <div className="space-y-3">
            {transactions.map((txn) => (
              <div key={txn.id} className="flex justify-between items-center p-3 bg-black/50 rounded-lg border border-[#1a4d5c]/20">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-white text-sm font-mono">{txn.session_id?.substring(0, 20)}...</span>
                    {txn.payment_intent_id && (
                      <span className="text-xs text-gray-500">PI: {txn.payment_intent_id.substring(0, 15)}...</span>
                    )}
                  </div>
                  <div className="text-gray-500 text-xs mt-1">
                    {new Date(txn.created_at).toLocaleString('es-ES')}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-white font-bold text-lg">${txn.amount?.toFixed(2) || '0.00'}</div>
                  <div className={`text-xs font-medium ${
                    txn.payment_status === 'paid' ? 'text-green-400' :
                    txn.payment_status === 'pending' ? 'text-yellow-400' :
                    'text-red-400'
                  }`}>
                    {txn.payment_status}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pending/Failed Alerts */}
      {(pendingPayments.length > 0 || failedPayments.length > 0) && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
          <h4 className="text-yellow-400 font-semibold mb-2">⚠️ Atención Requerida</h4>
          {pendingPayments.length > 0 && (
            <p className="text-sm text-yellow-300">
              {pendingPayments.length} pago(s) pendiente(s) de confirmación
            </p>
          )}
          {failedPayments.length > 0 && (
            <p className="text-sm text-red-300">
              {failedPayments.length} pago(s) fallido(s) - considerar reintentar o contactar al usuario
            </p>
          )}
        </div>
      )}

      {/* Refund Modal */}
      {showRefundModal && selectedPayment && (
        <RefundModal
          payment={selectedPayment}
          userId={user.id}
          onClose={() => {
            setShowRefundModal(false);
            setSelectedPayment(null);
          }}
          onSuccess={() => {
            setShowRefundModal(false);
            setSelectedPayment(null);
            refreshData();
          }}
        />
      )}
    </div>
  );
}

// Refund Modal Component
function RefundModal({ payment, userId, onClose, onSuccess }) {
  const { getToken } = useAdminAuth();
  const [refundType, setRefundType] = useState('full');
  const [amount, setAmount] = useState(payment.amount);
  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleRefund = async () => {
    if (!window.confirm(`¿Confirmar reembolso de $${amount}?`)) return;

    setIsLoading(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/payments/${payment.id}/refund`,
        {
          amount: refundType === 'full' ? payment.amount : parseFloat(amount),
          reason
        },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      alert('Reembolso procesado correctamente');
      onSuccess();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al procesar reembolso');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
      <div className="bg-[#0A0A0A] rounded-lg max-w-md w-full p-6 border border-[#1a4d5c]/30">
        <h3 className="text-xl font-bold text-white mb-4">Procesar Reembolso</h3>
        
        <div className="space-y-4">
          {/* Payment Info */}
          <div className="bg-black/50 rounded-lg p-4">
            <div className="text-sm text-gray-400">Pago Original</div>
            <div className="text-white font-bold text-lg">${payment.amount?.toFixed(2)}</div>
            <div className="text-xs text-gray-500 mt-1">ID: {payment.id}</div>
          </div>

          {/* Refund Type */}
          <div>
            <label className="block text-sm text-gray-300 mb-2">Tipo de Reembolso</label>
            <select
              value={refundType}
              onChange={(e) => {
                setRefundType(e.target.value);
                if (e.target.value === 'full') {
                  setAmount(payment.amount);
                }
              }}
              className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:border-[#D4AF37] focus:outline-none"
            >
              <option value="full">Reembolso Completo</option>
              <option value="partial">Reembolso Parcial</option>
            </select>
          </div>

          {/* Amount (if partial) */}
          {refundType === 'partial' && (
            <div>
              <label className="block text-sm text-gray-300 mb-2">Monto a Reembolsar</label>
              <input
                type="number"
                step="0.01"
                max={payment.amount}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:border-[#D4AF37] focus:outline-none"
              />
            </div>
          )}

          {/* Reason */}
          <div>
            <label className="block text-sm text-gray-300 mb-2">Motivo del Reembolso</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows="3"
              placeholder="Explica la razón del reembolso..."
              className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:border-[#D4AF37] focus:outline-none"
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
            >
              Cancelar
            </button>
            <button
              onClick={handleRefund}
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50"
            >
              {isLoading ? 'Procesando...' : 'Reembolsar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}